const FINAL_VERIFIED_STATUSES = new Set(["APPROVED"]);
const REVIEW_STATUSES = new Set(["KYC_SUBMITTED", "KYB_SUBMITTED"]);

export const getVerificationUiState = ({
  verificationStatus,
  identificationVerified,
  isOrganization,
}) => {
  const status = String(verificationStatus || "").trim().toUpperCase();
  const isVerified =
    Boolean(identificationVerified) || FINAL_VERIFIED_STATUSES.has(status);

  if (isVerified) {
    return {
      normalizedStatus: status || "APPROVED",
      isVerified: true,
      pillTone: "verified",
      pillLabel: "Verified",
      cardTone: "verified",
      cardTitle: "Verification complete",
      cardText: isOrganization
        ? "Your identity and business verification have been approved."
        : "Your identity verification has been approved.",
      actionLabel: null,
      showAction: false,
      modalTitle: "Verification Complete",
      modalText: isOrganization
        ? "Your identity and business verification have already been approved."
        : "Your identity verification has already been approved.",
      modalPrimaryLabel: null,
      showModalPrimary: false,
      modalSecondaryLabel: "Close",
    };
  }

  if (status === "REJECTED") {
    return {
      normalizedStatus: status,
      isVerified: false,
      pillTone: "rejected",
      pillLabel: "Rejected",
      cardTone: "rejected",
      cardTitle: "Verification needs attention",
      cardText:
        "Your last verification attempt was not approved. Review your details and start again.",
      actionLabel: "Restart Verification",
      showAction: true,
      modalTitle: "Verification Rejected",
      modalText:
        "Your previous verification attempt was not approved. Start a new verification to continue.",
      modalPrimaryLabel: "Restart Verification",
      showModalPrimary: true,
      modalSecondaryLabel: "Skip for now",
    };
  }

  if (status === "KYB_INITIALIZED") {
    return {
      normalizedStatus: status,
      isVerified: false,
      pillTone: "progress",
      pillLabel: "KYB Required",
      cardTone: "progress",
      cardTitle: "Continue business verification",
      cardText:
        "Your personal KYC step is complete. Continue with business verification to finish setup.",
      actionLabel: "Continue Business Verification",
      showAction: true,
      modalTitle: "Continue Business Verification",
      modalText:
        "Your personal verification is complete. Continue with business verification to unlock full access.",
      modalPrimaryLabel: "Continue Verification",
      showModalPrimary: true,
      modalSecondaryLabel: "Skip for now",
    };
  }

  if (status === "KYC_SUBMITTED" && isOrganization) {
    return {
      normalizedStatus: status,
      isVerified: false,
      pillTone: "progress",
      pillLabel: "KYB Required",
      cardTone: "progress",
      cardTitle: "Continue business verification",
      cardText:
        "Your personal KYC step is complete. Continue with business verification to finish setup.",
      actionLabel: "Continue Business Verification",
      showAction: true,
      modalTitle: "Continue Business Verification",
      modalText:
        "Your personal KYC step is complete. Continue with business verification to unlock full access.",
      modalPrimaryLabel: "Continue Verification",
      showModalPrimary: true,
      modalSecondaryLabel: "Skip for now",
    };
  }

  if (status === "INITIALIZED") {
    return {
      normalizedStatus: status,
      isVerified: false,
      pillTone: "progress",
      pillLabel: "In Progress",
      cardTone: "progress",
      cardTitle: "Continue verification",
      cardText:
        "Your verification has been started but not completed yet. Continue to finish your submission.",
      actionLabel: "Continue Verification",
      showAction: true,
      modalTitle: "Continue Verification",
      modalText:
        "Your verification has already been started. Continue where you left off to complete it.",
      modalPrimaryLabel: "Continue Verification",
      showModalPrimary: true,
      modalSecondaryLabel: "Skip for now",
    };
  }

  if (REVIEW_STATUSES.has(status)) {
    const isBusinessReview = status === "KYB_SUBMITTED";
    return {
      normalizedStatus: status,
      isVerified: false,
      pillTone: "pending",
      pillLabel: "In Review",
      cardTone: "pending",
      cardTitle: isBusinessReview
        ? "Business verification in review"
        : "Verification in review",
      cardText: isBusinessReview
        ? "Your business verification has been submitted and is currently under review."
        : "Your verification has been submitted and is currently under review.",
      actionLabel: null,
      showAction: false,
      modalTitle: isBusinessReview
        ? "Business Verification in Review"
        : "Verification in Review",
      modalText: isBusinessReview
        ? "Your business verification is currently under review. You do not need to take any action right now."
        : "Your verification is currently under review. You do not need to take any action right now.",
      modalPrimaryLabel: null,
      showModalPrimary: false,
      modalSecondaryLabel: "Close",
    };
  }

  return {
    normalizedStatus: status || "NOT_STARTED",
    isVerified: false,
    pillTone: "pending",
    pillLabel: "Not Verified",
    cardTone: "pending",
    cardTitle: "Verification required",
    cardText: isOrganization
      ? "Complete identity verification first, then business verification to unlock full access."
      : "Complete your identity verification to unlock full account access.",
    actionLabel: "Complete Verification",
    showAction: true,
    modalTitle: "Complete Verification",
    modalText: "Complete your verification to unlock full access to wallet and transfer features.",
    modalPrimaryLabel: "Start Verification",
    showModalPrimary: true,
    modalSecondaryLabel: "Skip for now",
  };
};
